/**
 * DatePicker component
 * Allows user to pick 'This week' 'Last week' 'This school year' or 'Custom date' from drop down.
 * Loads Calendar component if user chooses custom date range
 * Calls onDateChange callback prop when selection changes
 * User can pass in options list with translations
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import CalendarIcon from '@3plearning/3p-teacheradmin-assets/assets/icons/icon_calendar.svg';
import { DropdownMenu } from '@3plearning/react-components';
import styleable from 'react-styleable';
import moment from 'moment';

import CalendarTrans from '../calendar/calendar';
import {
    DEFAULT_DATE_OPTIONS_OBJ,
    CUSTOM_DATE_TIME_OBJ,
    CUSTOM_DATE,
    LAST_WEEK,
    THIS_SCHOOL_YEAR,
    THIS_WEEK,
    LIFETIME,
    CUSTOM_DATE_TIME,
    DATE_OPTIONS
} from './date-picker-constants';
import DateHelper from '../../../../helpers/date-helper';
import css from './date-picker-trans.scss';

/**
 * DatePickerTrans component
 * Allows user to pick 'This week' 'Last week' 'This school year' or 'Custom date' from drop down.
 * Loads Calendar component if user chooses custom date range
 * Calls onDateChange callback prop when selection changes
 */
class DatePickerTrans extends Component {
  static propTypes = {
      /** Range to limit the date selection to */
      academicYear: PropTypes.shape({
          startDate: PropTypes.string,
          endDate: PropTypes.string
      }).isRequired,

      /** Current selected date */
      currentDate: PropTypes.shape({
          type: PropTypes.oneOf(DATE_OPTIONS),
          startDate: PropTypes.string,
          endDate: PropTypes.string
      }).isRequired,

      /** Current selected date */
      locale: PropTypes.string,

      /** Options to display in dropdown menu */
      options: PropTypes.array,

      /** Callback when a date selection is made */
      onDateChange: PropTypes.func.isRequired,

      /** Flag whether custom date range should be mon-sun
     *  enabled by default
     */
      weeklyRangeOnly: PropTypes.bool,

      /** Props overrides for react-dates component
     * @see <a href="https://github.com/airbnb/react-dates#make-some-awesome-datepickers">react-dates props</a>
     */
      reactDatesConfig: PropTypes.object,

      /**
       * calendarLocaleData
       */
      calendarLocaleData: PropTypes.object,
      /** isLeft */
      isLeft: PropTypes.bool
  };

  static defaultProps = {
      options: DEFAULT_DATE_OPTIONS_OBJ
  };

  constructor(props) {
      super(props);
      const { weeklyRangeOnly, currentDate, locale, academicYear } = props;
      /**
       * SET LOCALE
       * */
      const localeCode = locale || 'en-AU';
      const path = `dayjs/locale/${localeCode.toLowerCase()}`;
      try {
          // eslint-disable-next-line global-require,import/no-dynamic-require
          require(path);
      } catch (e) {
          // eslint-disable-next-line global-require,import/no-dynamic-require
          require('dayjs/locale/en-au');
      }
      dayjs.locale(localeCode);
      moment.locale(locale);

      this.state = {
          selectedOption: this.getDefaultOption(),
          weeklyRangeOnly: weeklyRangeOnly !== undefined ? weeklyRangeOnly : true,
          selectedDateType: currentDate.type,
          useCustomDates: currentDate.type === CUSTOM_DATE,
          initialDate: academicYear
      };
  }

  getDefaultOption() {
      const {
          options,
          currentDate: { type }
      } = this.props;
      return options.find(option => option.value === type) || options[0];
  }

  /**
   * Runs when user changes value in dropdown
   * Calls onDateChange prop with newly selected date range (unless user chose custom date)
   */
  onDateChange = (option) => {
      this.setState({
          selectedOption: option,
          useCustomDates: false,
          selectedTimeRange: false,
          dateTimeSubmitted: false
      });

      let startDate;
      let endDate;

      switch (option.value) {
      case THIS_WEEK:
          ({ startDate, endDate } = DateHelper.getThisWeekStartEnd());
          break;
      case LAST_WEEK:
          ({ startDate, endDate } = DateHelper.getLastWeekStartEnd());
          break;
      case THIS_SCHOOL_YEAR:
      case LIFETIME:
          ({ startDate, endDate } = this.props.academicYear);
          break;
      case CUSTOM_DATE:
      case CUSTOM_DATE_TIME_OBJ.value:
          return;
      default:
          console.warn('Unhandled date type'); // eslint-disable-line no-console
      }

      this.props.onDateChange(option.value, startDate, endDate);
  };

  /**
   * Runs when user selects start & end dates in calendar component
   * Calls onDateChange prop with newly selected date range
   */
  onCustomDateChange = (startDate, endDate) => {
      if (startDate && endDate) {
          const {
              selectedOption: { value }
          } = this.state;
          this.props.onDateChange(value, startDate, endDate);
          const label = this.getSelectedLabel();
          this.setState({
              selectedOption: {
                  label,
                  value
              },
              useCustomDates: true
          });
      }
  };

  /**
   * Reverts date to previous selection if user cancelled selecting custom dates in calendar
   */
  onCustomDateClose = () => {
      // todo: i don't like this timeout but i couldn't get cancel functionality to
      // work without it
      setTimeout(() => {
          if (!this.state.useCustomDates) {
              this.setState({
                  selectedOption: this.getDefaultOption(),
                  useCustomDates: true,
                  hideSelectedTime: true
              });
          }
      });
  };

  /**
   * Generates the display text to show in the dropdown menu as the current selected option
   * Handles showing the date range if user chose custom date ie. 14 June 2018 - 28 June 2018
   */
  getSelectedLabel = () => {
      const { selectedOption, time, localeCode } = this.state;
      if (
          selectedOption.value === CUSTOM_DATE_TIME ||
      selectedOption.value === CUSTOM_DATE
      ) {
          const { startDate, endDate } = this.props.currentDate;
          const formatString = 'DD MMM YYYY';
          dayjs.locale(localeCode);
          const formattedStartDate = dayjs(startDate).format(formatString);
          const formattedEndDate = dayjs(endDate).format(formatString);
          return selectedOption.value === CUSTOM_DATE
              ? `${formattedStartDate} - ${formattedEndDate}`
              : `${formattedStartDate} ${time[0]} - ${formattedEndDate} ${time[1]}`;
      }

      return selectedOption.label;
  };

  render() {
      const {
          selectedOption,
          useCustomDates
      } = this.state;
      const {
          academicYear,
          reactDatesConfig,
          options,
          calendarLocaleData,
          isLeft
      } = this.props;
      const selectedDateType = selectedOption.value;
      /**
     * This boolean hides or shows the calendar component when required
     * for date range selection types for CustomDate or CustomDateTime
     * It is hidden again after selection of the range which may also include a time
     * @type {boolean}
     */
      const shouldDisplayCalendar = selectedDateType === CUSTOM_DATE && !useCustomDates;
      return (
          <div className={css['date-picker-wrapper']} >
              {shouldDisplayCalendar && (
                  <CalendarTrans
                      range={academicYear}
                      reactDatesConfig={reactDatesConfig}
                      onDatesPicked={this.onCustomDateChange}
                      onClose={this.onCustomDateClose}
                      localeData={calendarLocaleData}
                      isLeft={isLeft}
                  />
              )}
              {!shouldDisplayCalendar && (
                  <DropdownMenu
                      icon={CalendarIcon}
                      options={options}
                      selectedOption={{
                          label: this.getSelectedLabel(),
                          value: selectedDateType
                      }}
                      onSelectOption={option => this.onDateChange(option)}
                  />
              )}
          </div>
      );
  }
}

export default styleable(css)(DatePickerTrans);



// WEBPACK FOOTER //
// ./components/date-picker/components/date-picker-trans/date-picker-trans.js